import React from 'react';
import { createSelector } from 'reselect';

import { DecodedRouteParams, Route } from 'config/routes';
import ProfileIcon from 'assets/svg/profile.svg';
import ListIcon from 'assets/svg/list.svg';
import ListThickIcon from 'assets/svg/navigation/list.svg';
import HeartIcon from 'assets/svg/navigation/heart.svg';
import AgentListingsIcon from 'assets/svg/project/building.svg';
import LogoutIcon from 'assets/svg/log-out.svg';
import LogoutMobileIcon from 'assets/svg/navigation/logout.svg';
import ViewProfileIcon from 'assets/svg/seller.svg';
import MyHomesIcon from 'assets/svg/saved-addresses-nav.svg';
import MyHomesMobileIcon from 'assets/svg/navigation/saved-addresses.svg';
import MyCommuteIcon from 'assets/svg/commute-profile-menu.svg';
import WorkLocationIcon from 'assets/svg/navigation/work-location.svg';
import ProfSystemIcon from 'assets/svg/navigation/chart-pie-24.svg';
import TagIcon from 'assets/svg/price-us.svg';
import EditIcon from 'assets/svg/edit-tooltip.svg';
import PlusThickIcon from 'assets/svg/navigation/plus.svg';
import PlusIcon from 'assets/svg/add.svg';
import UserIcon from 'assets/svg/navigation/user.svg';
import HouseIcon from 'assets/svg/navigation/house.svg';
import KeyIcon from 'assets/svg/navigation/key.svg';
import ArmchairIcon from 'assets/svg/navigation/armchair.svg';
import DollarSignIcon from 'assets/svg/navigation/dollar-sign.svg';
import BackhoeIcon from 'assets/svg/navigation/backhoe.svg';
import HandsIcon from 'assets/svg/handshake.svg';
import DealsIcon from 'assets/svg/key-ic.svg';
import BellRingingIcon from 'assets/svg/bell-ringing.svg';
import PushNotificationsIcon from 'assets/svg/push-notifications.svg';
import CommercialBuildingIcon from 'assets/svg/navigation/commercial-building.svg';
import AgentConsoleHomeIcon from 'assets/svg/agent-console-icon.svg';
import InvestorsIcon from 'assets/svg/chart-bar.svg';
import InvestorsMobileIcon from 'assets/svg/chart-bar-mobile.svg';
import MeterReportIcon from 'assets/svg/chart-line-up.svg';
import {
  isConnectedUserSelector,
  isAgentConsoleUserSelector,
  approvedAgentSelector,
  isInactiveAgentSelector,
  isProExpertUserSelector,
  isPrivateUserSelector,
} from 'store/state/domainData/selectors';
import { ProfileTab } from 'components/profile/Profile';
import { DealType, MarketplaceType } from 'utils/entities';
import config from 'config';
import { Seller } from 'components/filters/types';
import { pushNotificationsEnabledSelector } from 'store/state/selectors/pushNotificationsStatus';
import { pushNotificationsFeatureEnabled } from 'utils/pushNotificationsFeatureEnabled';
import { savedSearchesCountSelector } from 'store/state/selectors/savedSearchPage';
import { getProExpertUrl } from 'helpers/proExpert';
import { abTestsStateSelector } from 'store/state/ab-tests/selectors';
import { ProExpertLink } from 'config/abTests';


export enum UserActionName {
  Home = 'home',
  SavedSearches = 'savedSearches',
  Logout = 'logout',
  ViewAgentPage = 'viewAgentPage',
  EditAgentPage = 'editAgentPage',
  MyHomes = 'myHomes',
  MyCommute = 'myCommute',
  ProfileSettings = 'profileSettings',
  MyListings = 'myListings',
  SavedListings = 'savedListings',
  AgentCommercialBulletins = 'agentCommercialBulletins',
  UploadBulletin = 'uploadBulletin',
  Billing = 'billing',
  ResidentialMarketplaceBuy = 'residentialMarketplaceBuy',
  ResidentialMarketplaceRent = 'residentialMarketplaceRent',
  ResidentialMarketplaceNewProjects = 'residentialMarketplaceNewProjects',
  CommercialLandingPage = 'commercialLandingPage',
  ExplorePage = 'explorePage',
  DeveloperIndex = 'developerIndex',
  MortgageOffices = 'mortgageOffices',
  PushSettings = 'pushSettings',
  Deals = 'deals',
  AgentConsoleHome = 'agentConsoleHome',
  Investors = 'investors',
  ProExpert = 'proExpert',
  ProfSystem = 'profSystem',
  MeterReport = 'meterReport',
}

export interface UserActionEventData {
  name: string;
  category: string;
  event: unknown;
}

export interface UserAction {
  name: UserActionName;
  route?: Route;
  customRoute?: string;
  params?: Partial<DecodedRouteParams>;
  hasWarning?: boolean;
  count?: number;
  isBeta?: boolean;
  isNew?: boolean;
  eventData?: UserActionEventData;
}

export const actionToIcon: Record<UserActionName, React.ReactNode> = {
  [UserActionName.Home]: null,
  [UserActionName.ProfileSettings]: <ProfileIcon width={24} height={24} />,
  [UserActionName.SavedSearches]: <BellRingingIcon />,
  [UserActionName.MyListings]: <ListIcon width={24} height={24} />,
  [UserActionName.SavedListings]: null,
  [UserActionName.AgentCommercialBulletins]: <AgentListingsIcon width={24} height={24} />,
  [UserActionName.Logout]: <LogoutIcon width={24} height={24} />,
  [UserActionName.ViewAgentPage]: <ViewProfileIcon width={24} height={24} />,
  [UserActionName.MyHomes]: <MyHomesIcon width={24} height={24} />,
  [UserActionName.MyCommute]: <MyCommuteIcon width={24} height={24} />,
  [UserActionName.Billing]: <TagIcon width={24} height={24} />,
  [UserActionName.EditAgentPage]: <EditIcon width={24} height={24} />,
  [UserActionName.UploadBulletin]: <PlusIcon width={24} height={24} />,
  [UserActionName.ResidentialMarketplaceBuy]: null,
  [UserActionName.ResidentialMarketplaceRent]: null,
  [UserActionName.ResidentialMarketplaceNewProjects]: null,
  [UserActionName.CommercialLandingPage]: null,
  [UserActionName.ExplorePage]: null,
  [UserActionName.DeveloperIndex]: null,
  [UserActionName.MortgageOffices]: <HandsIcon width={24} height={24} />,
  [UserActionName.PushSettings]: null,
  [UserActionName.Deals]: <DealsIcon width={20} height={20} />,
  [UserActionName.AgentConsoleHome]: <AgentConsoleHomeIcon />,
  [UserActionName.Investors]: <InvestorsIcon />,
  [UserActionName.ProExpert]: <ProfSystemIcon />,
  [UserActionName.ProfSystem]: <ProfSystemIcon />,
  [UserActionName.MeterReport]: <MeterReportIcon />,
};

export const actionToIconMobile: Record<UserActionName, React.ReactNode> = {
  [UserActionName.Home]: <HouseIcon width={24} />,
  [UserActionName.ProfileSettings]: <UserIcon width={24} />,
  [UserActionName.SavedSearches]: <BellRingingIcon width={24} />,
  [UserActionName.MyListings]: <ListThickIcon width={24}  />,
  [UserActionName.SavedListings]: <HeartIcon width={24} />,
  [UserActionName.AgentCommercialBulletins]: <AgentListingsIcon width={24} />,
  [UserActionName.Logout]: <LogoutMobileIcon width={24} />,
  [UserActionName.ViewAgentPage]: <ViewProfileIcon width={24} />,
  [UserActionName.MyHomes]: <MyHomesMobileIcon width={24} />,
  [UserActionName.MyCommute]: <WorkLocationIcon width={24} />,
  [UserActionName.Billing]: <TagIcon width={24} />,
  [UserActionName.EditAgentPage]: <EditIcon width={24} />,
  [UserActionName.UploadBulletin]: <PlusThickIcon width={24} />,
  [UserActionName.ResidentialMarketplaceBuy]: <KeyIcon width={24} />,
  [UserActionName.ResidentialMarketplaceRent]: <ArmchairIcon width={24} />,
  [UserActionName.ResidentialMarketplaceNewProjects]: <MyHomesMobileIcon width={24} />,
  [UserActionName.CommercialLandingPage]: <CommercialBuildingIcon width={24} />,
  [UserActionName.ExplorePage]: <DollarSignIcon width={24} />,
  [UserActionName.DeveloperIndex]: <BackhoeIcon width={24} />,
  [UserActionName.MortgageOffices]: <HandsIcon width={24} />,
  [UserActionName.PushSettings]: <PushNotificationsIcon width={24} />,
  [UserActionName.Deals]: <DealsIcon width={22} height={22} />,
  [UserActionName.AgentConsoleHome]: <AgentConsoleHomeIcon width={24} height={24} />,
  [UserActionName.Investors]: <InvestorsMobileIcon />,
  [UserActionName.ProExpert]: <ProfSystemIcon />,
  [UserActionName.ProfSystem]: <ProfSystemIcon />,
  [UserActionName.MeterReport]: <MeterReportIcon />,
};


export const desktopUserActionsSelector = createSelector([
  isConnectedUserSelector,
  isAgentConsoleUserSelector,
  approvedAgentSelector,
  isInactiveAgentSelector,
  savedSearchesCountSelector,
  isProExpertUserSelector,
  isPrivateUserSelector,
  abTestsStateSelector,
], (isUser, isAgentConsoleUser, isApprovedAgent, isInactiveAgent, savedSearchesCount, isProExpertUser, isPrivateUser, abTests): UserAction[] => [
  (isApprovedAgent ? null : {
    name: UserActionName.ProfileSettings,
    route: Route.Profile,
  }),
  {
    name: UserActionName.Investors,
    route: Route.InvestorsHomePage,
    isBeta: true,
    params: {
      source: 'profile_menu',
    },
  },
  (isPrivateUser ? {
    name: UserActionName.MeterReport,
    customRoute: 'https://madlan.co.il/propertyevaluation/',
    isNew: true,
    params: {
      source: 'profile_menu',
    },
    eventData: {
      name: 'page_appraisal_platform_click',
      category: 'page',
      event: {
        source: 'profile_menu',
      },
    },
  } : null),
  (isProExpertUser && abTests.proExpertLink === ProExpertLink.modeB ? {
    name: UserActionName.ProExpert,
    customRoute: getProExpertUrl(),
    isBeta: true,
    params: {
      source: 'homepage_sidebar_menu',
    },
  } : null),
  {
    name: UserActionName.SavedSearches,
    route: Route.SavedSearchesPage,
    count: savedSearchesCount,
  },
  {
    name: UserActionName.MyHomes,
    route: Route.MyHomes,
  },
  (isUser && !isAgentConsoleUser && !isInactiveAgent ? {
    name: UserActionName.MyListings,
    route: Route.ManageBulletins,
  } : null),
  (isUser && !isAgentConsoleUser && !isInactiveAgent ? {
    name: UserActionName.UploadBulletin,
    route: Route.UploadBulletin,
    params: {
      source: 'profile_menu',
    },
  } : null),
].filter(Boolean));

export const mobileProfileMenuActionsSelector = createSelector([
  isConnectedUserSelector,
  pushNotificationsEnabledSelector,
  isAgentConsoleUserSelector,
  approvedAgentSelector,
  isInactiveAgentSelector,
], (isUser, pushEnabled, isAgentConsoleUser, isApprovedAgent, isInactiveAgent): UserAction[] => [
  (isApprovedAgent ? null : {
    name: UserActionName.ProfileSettings,
    route: Route.Profile,
  }),
  (isAgentConsoleUser || isInactiveAgent ? {
    name: UserActionName.AgentConsoleHome,
    route: Route.AgentConsoleHomePage,
  } : null),
  (isUser && !isAgentConsoleUser && !isInactiveAgent ? {
    name: UserActionName.UploadBulletin,
    route: Route.UploadBulletin,
    params: {
      source: 'profile_menu',
    },
  } : null),
  (!isAgentConsoleUser && !isInactiveAgent && isUser ? {
    name: UserActionName.SavedListings,
    route: Route.Shortlist,
  } : null),
  (!isAgentConsoleUser && !isInactiveAgent && isUser ? {
    name: UserActionName.MyListings,
    route: Route.ManageBulletins,
  } : null),
  (pushNotificationsFeatureEnabled() ? ({
    name: UserActionName.PushSettings,
    route: Route.PushSettings,
    hasWarning: !pushEnabled,
  }) : null),
  {
    name: UserActionName.SavedSearches,
    route: Route.SavedSearchesPage,
  },
  {
    name: UserActionName.MyHomes,
    route: Route.MyHomes,
  },
  (!isAgentConsoleUser && !isInactiveAgent ? {
    name: UserActionName.MyCommute,
    route: Route.Profile,
    params: { initialTab: ProfileTab.Personal },
  } : null),
].filter(Boolean));

export const mobileMoreMenuActions: UserAction[] = [
  {
    name: UserActionName.Home,
    route: Route.Home,
  },
  {
    name: UserActionName.Investors,
    route: Route.InvestorsHomePage,
    isBeta: true,
    params: {
      source: 'homepage_sidebar_menu',
    },
  },
  {
    name: UserActionName.MeterReport,
    customRoute: 'https://madlan.co.il/propertyevaluation/',
    isBeta: false,
    isNew: true,
    eventData: {
      name: 'homepage_appraisal_platform_click',
      category: 'homepage',
      event: {
        source: 'homepage_sidebar_menu',
      },
    },
  },
  {
    name: UserActionName.ResidentialMarketplaceBuy,
    route: Route.Search,
    params: { id: undefined, dealType: DealType.Buy, term:  [ config.cityTerm ], marketplace: MarketplaceType.Residential, filters: {} },
  },
  {
    name: UserActionName.ResidentialMarketplaceNewProjects,
    route: Route.Search,
    params: { id: undefined, dealType: DealType.Buy, term:  [ config.cityTerm ], marketplace: MarketplaceType.Residential, filters: { seller: [ Seller.Developer ] } },
  },
  {
    name: UserActionName.ResidentialMarketplaceRent,
    route: Route.Search,
    params: { id: undefined, dealType: DealType.Rent, term:  [ config.cityTerm ], marketplace: MarketplaceType.Residential, filters: {} },
  },
  {
    name: UserActionName.CommercialLandingPage,
    route: Route.CommercialMarketLanding,
  },
  {
    name: UserActionName.ExplorePage,
    route: Route.CheckAddress,
  },
  {
    name: UserActionName.MortgageOffices,
    route: Route.MortgageOfficesSearchPage,
  },
  {
    name: UserActionName.DeveloperIndex,
    route: Route.DevelopersSearchPage,
  },
];
